import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import teamImage from '../assets/images/group_of_5_3_with_sultansoft_branded_shirts.svg';

const CompanyStory = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const values = [
    {
      title: 'African Innovation',
      description: 'We believe in the power of African talent and innovation. Our solutions are built by Africans, for Africans, addressing the unique challenges and opportunities of our continent.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      title: 'Enterprise Excellence',
      description: 'We deliver enterprise-grade solutions with the reliability, security, and scalability that businesses demand. Our technology infrastructure meets the highest industry standards.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
    },
    {
      title: 'Future-Focused',
      description: 'We\'re not just building for today—we\'re architecting the digital infrastructure that will power Africa\'s economic transformation for decades to come.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
    },
  ];

  const milestones = [
    { year: '2024', title: 'Founded', description: 'Sultansoft established with a vision to transform African technology' },
    { year: '2024', title: 'Product Development', description: 'Three flagship products in development: Academy, Trading Platform, and Telco Solutions' },
    { year: '2025', title: 'Launch Phase', description: 'Planned launch of Sultansoft Academy and African Stock Exchange Platform' },
    { year: '2026', title: 'Expansion', description: 'Pan-African expansion with enterprise telco solutions' },
  ];

  return (
    <section id="about" className="section bg-white">
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-4xl mx-auto mb-20"
        >
          <h2 className="heading text-navy-950 mb-6">
            Who We Are
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Sultansoft was born from a simple yet powerful belief: Africa deserves world-class technology 
            solutions that are built by Africans, for Africans. We're not just another software company—we're 
            architects of Africa's digital future.
          </p>
        </motion.div>

        {/* Mission Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-gradient-to-r from-navy-950 to-navy-800 rounded-3xl p-12 text-center text-white mb-20"
        >
          <h3 className="text-3xl font-display font-bold mb-6">
            Our Mission
          </h3>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            To democratize access to world-class technology solutions across Africa, empowering businesses, 
            individuals, and communities to thrive in the digital economy through innovative, scalable, 
            and locally-relevant technology platforms.
          </p>
        </motion.div>

        {/* Core Values */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20"
        >
          {values.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
              className="card text-center group hover:scale-105 transition-transform duration-300"
            >
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-r from-crimson-500 to-crimson-600 rounded-2xl flex items-center justify-center text-white group-hover:shadow-glow transition-shadow duration-300">
                {value.icon}
              </div>
              <h3 className="text-xl font-display font-bold text-navy-950 mb-4">
                {value.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {value.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="bg-gray-50 rounded-3xl p-12"
        >
          <h3 className="text-3xl font-display font-bold text-center text-navy-950 mb-12">
            Our Journey
          </h3>
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-cyan-500 to-coral-500 rounded-full hidden md:block" />
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  transition={{ duration: 0.6, delay: 1.0 + index * 0.1 }}
                  className={`flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  <div className={`w-full md:w-1/2 ${index % 2 === 0 ? 'md:pr-8' : 'md:pl-8'}`}>
                    <div className="card">
                      <div className="text-2xl font-display font-bold text-crimson-600 mb-2">
                        {milestone.year}
                      </div>
                      <h4 className="text-xl font-display font-bold text-navy-950 mb-3">
                        {milestone.title}
                      </h4>
                      <p className="text-gray-600">
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* Timeline Dot */}
                  <div className="hidden md:flex w-4 h-4 bg-white border-4 border-crimson-500 rounded-full absolute left-1/2 transform -translate-x-1/2 z-10" />
                  
                  <div className="w-full md:w-1/2" />
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Team Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="mt-20"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-display font-bold text-navy-950 mb-4">
              Meet Our Team
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A diverse group of passionate engineers and innovators dedicated to building Africa's digital future.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
              transition={{ duration: 0.6, delay: 1.4 }}
            >
              <img
                src={teamImage}
                alt="Sultansoft team members"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
              transition={{ duration: 0.6, delay: 1.6 }}
              className="space-y-6"
            >
              <h4 className="text-2xl font-display font-bold text-navy-950">
                Passionate About Innovation
              </h4>
              <p className="text-gray-600 leading-relaxed">
                Our team combines deep technical expertise with a genuine passion for solving 
                real-world problems. We believe in the power of technology to transform 
                communities and create opportunities across Africa.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <div className="text-2xl font-display font-bold text-crimson-600 mb-1">5+</div>
                  <div className="text-sm text-gray-600">Team Members</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <div className="text-2xl font-display font-bold text-crimson-600 mb-1">50+</div>
                  <div className="text-sm text-gray-600">Years Experience</div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CompanyStory;
